import { Layout } from 'antd';
import { observer } from 'mobx-react';
import { useMst } from '@models/Root';
import { HSMSLoading } from '@components/shared';
import { useEffect, useState } from 'react';
import Router, { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import { authProvider } from '@lib/auth';
import Error from 'next/error';

const HSMSSidebar = dynamic(() => import('@components/app/HSMSSidebar').then((module) => module.HSMSSidebar), {
  ssr: false,
});
const HSMSTopMenu = dynamic(() => import('@components/app/HSMSTopMenu').then((module) => module.HSMSTopMenu), {
  ssr: false,
});

export const AppLayout = observer(({ children }) => {
  const { user, student, isLoading, reset } = useMst();
  const router = useRouter();
  const [timeOutId, setTimeOutId] = useState<NodeJS.Timeout | null>(null);
  const [isFirebaseReady, setIsFirebaseReady] = useState<boolean | null>(null);
  const [isRouteAllowed, setIsRouteAllowed] = useState<boolean | null>(null);

  useEffect(() => {
    if (!(student.id || user.id) && !(router.pathname === '/login' || router.pathname === '/forgot-password')) {
      Router.push('/login').then();
    } else if ((student.id || user.id) && (router.pathname === '/login' || router.pathname === '/forgot-password')) {
      // If student and not filled familiarization form, direct to that page
      if (student.id && !student.familiarizationFormSubmitted) {
        Router.push('/student-familiarization-form').then();
      } else if (student.id && student.status === 'withdrawn' && !student.exitInterviewFormSubmitted) {
        Router.push('/exit-interview').then();
      } else {
        Router.push('/').then();
      }
    }
  });

  // Check route permissions
  useEffect(() => {
    if (student.id && !student.familiarizationFormSubmitted) {
      Router.push('/student-familiarization-form').then();
    } else if (student.id && student.status === 'withdrawn' && !student.exitInterviewFormSubmitted) {
      Router.push('/exit-interview').then();
    } else if (router.pathname !== '/' && router.pathname !== '/login' && router.pathname !== '/forgot-password') {
      let routeAllowed = false;

      if (user.id !== '') {
        routeAllowed = Boolean(user.routes.find((route) => `/${route.path}` === router.pathname));
      } else if (student.id) {
        routeAllowed = Boolean(student.routes.find((route) => `/${route.path}` === router.pathname));
      }

      setIsRouteAllowed(routeAllowed);
    } else {
      setIsRouteAllowed(true);
    }
  }, [router.pathname]);

  useEffect(() => {
    // listen for auth state changes
    const unsubscribe = authProvider.isUserLoggedIn(() => {
      setIsFirebaseReady(true);
    });
    // unsubscribe to the listener when un-mounting
    return () => {
      unsubscribe();
    };
  });

  useEffect(() => {
    if (user.id) {
      authProvider.setTimeOutLogout(reset).then((timeOut) => {
        setTimeOutId(timeOut);
      });
    }

    return () => {
      clearTimeout(timeOutId);
    };
  }, [user.id]);

  if (
    !isFirebaseReady ||
    isLoading ||
    ((student.id || user.id) && (router.pathname === '/login' || router.pathname === '/forgot-password'))
  ) {
    return <HSMSLoading />;
  }

  if (student.id || user.id) {
    return (
      <>
        <HSMSSidebar />

        <Layout>
          <HSMSTopMenu />

          {isRouteAllowed === false ? <Error statusCode={404} /> : children}
        </Layout>
      </>
    );
  }

  if (router.pathname === '/login' || router.pathname === '/forgot-password') {
    return <>{children}</>;
  }

  return <div />;
});
